/**
 * Per-key cooldowns for system toasts that would otherwise repeat on every
 * store refresh. Persisted so a relaunch does not re-announce the same issue.
 * Desktop callers namespace keys by account because desktop sign-in/out does
 * not pass through the web auth hook that resets this gate.
 */
const STORAGE_KEY = "denker.toast_cooldowns";
const RETENTION_MS = 7 * 24 * 60 * 60_000;

let memory: Record<string, number> = {};

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readCooldowns(): Record<string, number> {
  const store = storage();
  if (!store) return memory;
  try {
    const parsed: unknown = JSON.parse(store.getItem(STORAGE_KEY) ?? "{}");
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return memory;
  }
}

function writeCooldowns(values: Record<string, number>): void {
  memory = values;
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // The in-memory copy still gates this session.
  }
}

export function claimToastCooldown(
  key: string,
  cooldownMs: number,
  now = Date.now(),
): boolean {
  const values = readCooldowns();
  const last = values[key];
  if (typeof last === "number" && now >= last && now - last < cooldownMs) {
    return false;
  }
  const kept = Object.fromEntries(
    Object.entries(values).filter(
      ([, at]) => typeof at === "number" && now - at < RETENTION_MS,
    ),
  );
  writeCooldowns({ ...kept, [key]: now });
  return true;
}

export function resetToastCooldowns(): void {
  memory = {};
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort session cleanup.
  }
}
